/**
 * TripForm — Kopf-Daten einer Dienstreise (RK-7).
 *
 * Erfasst Anlass, Zielort, (optionale) Tätigkeitsstätte, Ab-/Rückreise mit
 * Datum+Uhrzeit sowie die Pflicht-Checkbox „erste Tätigkeitsstätte" (§4.1
 * `isFirstWorkplace` — `true` blockiert das spätere Abrechnen hart,
 * `calculateTravelAllowances` liefert dann `firstWorkplaceTrip`).
 *
 * Reine Präsentationskomponente: hält keinen eigenen State, meldet jede
 * Änderung über `onChange` an den Aufrufer (`TravelExpensesPage`), der den
 * kompletten Bearbeitungszustand einer Reise hält (Single Source of Truth).
 *
 * @module components/travel/TripForm
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../icons';

/** Kopf-Felder einer Reise, wie sie `TripForm` bearbeitet (§4.1 Teilmenge). */
export interface TripFormValue {
  purpose: string;
  destinationCity: string;
  venueLabel: string | null;
  isFirstWorkplace: boolean;
  departureDateISO: string;
  departureTime: string;
  returnDateISO: string;
  returnTime: string;
}

interface TripFormProps {
  value: TripFormValue;
  onChange: (value: TripFormValue) => void;
  /** `true` bei nicht-editierbaren (settled/cancelled) Reisen — deaktiviert alle Felder. */
  disabled?: boolean;
}

const inputClass =
  'w-full bg-surface border border-hairline rounded-lg px-3 py-2 text-sm text-primary ' +
  'placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';
const labelClass = 'block text-xs font-medium text-muted mb-1';

/**
 * Formular für die Kopf-Daten einer Dienstreise.
 */
const TripForm: React.FC<TripFormProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation('travel');

  const set = <K extends keyof TripFormValue>(key: K, fieldValue: TripFormValue[K]): void => {
    onChange({ ...value, [key]: fieldValue });
  };

  return (
    <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5 space-y-4">
      <h2 className="text-sm font-semibold text-primary">{t('form.headerTitle')}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor="trip-purpose">
            {t('form.purpose')}
          </label>
          <input
            id="trip-purpose"
            type="text"
            className={inputClass}
            value={value.purpose}
            onChange={(e) => set('purpose', e.target.value)}
            placeholder={t('form.purposePlaceholder')}
            disabled={disabled}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="trip-destination">
            {t('form.destinationCity')}
          </label>
          <input
            id="trip-destination"
            type="text"
            className={inputClass}
            value={value.destinationCity}
            onChange={(e) => set('destinationCity', e.target.value)}
            placeholder={t('form.destinationCityPlaceholder')}
            disabled={disabled}
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor="trip-venue">
          {t('form.venueLabel')}
        </label>
        <input
          id="trip-venue"
          type="text"
          className={inputClass}
          value={value.venueLabel ?? ''}
          onChange={(e) => set('venueLabel', e.target.value.length > 0 ? e.target.value : null)}
          placeholder={t('form.venueLabelPlaceholder')}
          disabled={disabled}
        />
        <p className="text-xs text-faint mt-1">{t('form.venueLabelHint')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-muted mb-1">{t('form.departure')}</legend>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              aria-label={t('form.departureDate')}
              className={inputClass}
              value={value.departureDateISO}
              onChange={(e) => set('departureDateISO', e.target.value)}
              disabled={disabled}
            />
            <input
              type="time"
              aria-label={t('form.departureTime')}
              className={inputClass}
              value={value.departureTime}
              onChange={(e) => set('departureTime', e.target.value)}
              disabled={disabled}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-muted mb-1">{t('form.return')}</legend>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              aria-label={t('form.returnDate')}
              className={inputClass}
              value={value.returnDateISO}
              onChange={(e) => set('returnDateISO', e.target.value)}
              disabled={disabled}
            />
            <input
              type="time"
              aria-label={t('form.returnTime')}
              className={inputClass}
              value={value.returnTime}
              onChange={(e) => set('returnTime', e.target.value)}
              disabled={disabled}
            />
          </div>
        </fieldset>
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={value.isFirstWorkplace}
          onChange={(e) => set('isFirstWorkplace', e.target.checked)}
          disabled={disabled}
        />
        <span className="text-sm text-secondary">{t('form.isFirstWorkplace')}</span>
      </label>

      {value.isFirstWorkplace && (
        <div className="flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg px-3 py-2">
          <IconWarning size={14} className="mt-0.5 shrink-0 text-[var(--tone-danger-fg)]" />
          <p className="text-xs text-[var(--tone-danger-fg)]">{t('form.isFirstWorkplaceWarning')}</p>
        </div>
      )}
    </div>
  );
};

export default TripForm;
